<script>
import { mapGetters } from 'vuex'
import { GlobalBus } from '@services/bus'
import { FEATURES, MODAL } from '@constants/Text'
import { deveVerSoFeeFixo } from '@utils/escritorioFeeFixoMigracao'
import { SHOW_ATENDIMENTO_BLACKLIST } from '@modules/atendimento/constants/Text.js'
import { apiAtendimento } from '@modules/atendimento/api/apiAtendimento'
import ConversationList from '@modules/atendimento/components/conversation-list/conversation-list.vue'
import ChatWindow from '@modules/atendimento/components/chat-window/chat-window.vue'
import ModalNovaConversa from '@modules/atendimento/modal/modal-nova-conversa/modal-nova-conversa.vue'
import ModalImportarHistorico from '@modules/atendimento/modal/modal-importar-historico/modal-importar-historico.vue'
import ModalGerenciarRespostas from '@modules/atendimento/modal/modal-gerenciar-respostas/modal-gerenciar-respostas.vue'
import ModalRespostaRapida from '@modules/atendimento/modal/modal-resposta-rapida/modal-resposta-rapida.vue'
import ModalTransferirConversa from '@modules/atendimento/modal/modal-transferir-conversa/modal-transferir-conversa.vue'
import ModalVincularContato from '@modules/atendimento/modal/modal-vincular-contato/modal-vincular-contato.vue'
import {
  loadConexoes as fetchConexoes,
  pickSelectedIfConexaoRemovida,
  isDestructiveConexaoAction,
  fireSyncChatsForConectadas,
  fireRefreshChatsListForVisible,
  buildRefreshFeedback,
  scheduleDeletedConversasRetries,
  createSyncReloadTimer,
  createConversasPoller,
  createConnectRetries,
  registerAtendimentoListeners,
} from '@modules/atendimento/services/atendimento.service'
import {
  countNonRootDialogs,
  maxDialogZ,
} from '@modules/atendimento/utils/dialog-z-index-helpers'

const POLL_INTERVAL_MS = 5000

export default {
  name: 'AtendimentoPopup',
  components: {
    ConversationList,
    ChatWindow,
    ModalNovaConversa,
    ModalImportarHistorico,
    ModalGerenciarRespostas,
    ModalRespostaRapida,
    ModalTransferirConversa,
    ModalVincularContato,
  },
  data() {
    return {
      isOpen: false,
      conexoes: [],
      pendingClienteApp: null,
      // Default baixo (só acima da view). MutationObserver (debounced 50ms)
      // atualiza zIndex/contagens reativamente conforme dialogs Vuetify abrem/fecham.
      zIndex: 6,
      activeDialogCount: 0,
      nonRootDialogCount: 0,
      dialogObserver: null,
      refreshTimer: null,
      syncingChats: false,
      refreshingChats: false,
    }
    // Factories do atendimento.service ficam como propriedades não-reativas
    // da instância (this._poller, this._syncTimer, this._unregisterGlobalBus),
    // gerenciadas em created/beforeDestroy.
  },
  computed: {
    ...mapGetters('atendimento', ['conversas', 'selectedConversa']),
    shouldRender() {
      // Atendimento é ferramenta do PROFISSIONAL (conversar com clientes via
      // WhatsApp/chat) — nunca para o cliente final.
      if (this.$currentUser().IS_CLIENTE) return false
      // Escritório Fee Fixo Migração, não-gestor: só usa o módulo Fee Fixo
      // (achado do usuário, 12/08/2026) — o popup de atendimento não faz
      // parte dessa experiência.
      if (deveVerSoFeeFixo(this.$currentUser())) return false
      // Não renderiza se user não tem feature. Também não renderiza na
      // aba crm.atendimento, onde o próprio view-atendimento.vue já
      // está ativo (montar popup ali causaria double-mount dos 6 modais
      // do atendimento e conflito de GlobalBus).
      if (!this.$canAccess(FEATURES.ATENDIMENTO)) return false

      const routeName = this.$route && this.$route.name
      if (SHOW_ATENDIMENTO_BLACKLIST.includes(routeName)) return false
      return true
    },
    // Popup só aparece quando não há modais "acessórios". Exceção: modal raiz
    // de CRM (`content-class="crm-root-modal"`) NÃO esconde o popup — único
    // caso em que o FAB continua acima pro user voltar ao atendimento sem
    // fechar o contexto. Qualquer outro modal (termos, QR, confirmação) esconde.
    shouldShowPopup() {
      return this.nonRootDialogCount === 0
    },
    hasSelectedConversa() {
      return !!this.selectedConversa
    },
    hasConexoes() {
      return Array.isArray(this.conexoes) && this.conexoes.length > 0
    },
    // Tema flatWhite pro ChatWindow (sem ViewAtendimento ancestral as CSS vars
    // não chegariam). Mesmo padrão de omni-atendimento.vue.
    panelCssVars() {
      return {
        '--panel-bg': '#FFFFFF',
        '--panel-bg-hover': '#F5F5F5',
        '--panel-scroll-track': '#FFFFFF',
      }
    },
    toggleAriaLabel() {
      return this.isOpen ? 'Fechar mensagens' : 'Abrir mensagens'
    },
    // Botão "Expandir mensagens" só faz sentido se o user tem feature
    // CRM E está dentro do módulo CRM. Em outros módulos, navegar pra
    // crm.atendimento seria inconsistente — o popup atende sem sair.
    canOpenFullAtendimento() {
      if (!this.$canAccess(FEATURES.CRM)) return false
      const name = this.$route && this.$route.name
      if (!name) return false
      return name === 'crm' || name.startsWith('crm.')
    },
  },
  watch: {
    // Quando conversas carregam e havia pedido pendente pra abrir direto no
    // chat de um cliente específico, tenta resolver agora.
    conversas(newList) {
      if (!this.pendingClienteApp) return
      if (!newList || newList.length === 0) return
      this.trySelectByClienteApp(this.pendingClienteApp)
    },
  },
  created() {
    // MODAL.ATENDIMENTO_POPUP fica direto no componente — o service só
    // lida com ACTIONs de atendimento (não com modais). Os 4 ACTIONs
    // restantes (HISTORICO_IMPORTADO, CONVERSA_CRIADA, CONEXAO_CONECTADA,
    // CONEXAO_ATUALIZADA) vão via registerAtendimentoListeners.
    //
    // CONEXAO_ATUALIZADA é fallback cinto-e-suspensório: quando o painel
    // do popup está desmontado (v-dialog aberto) o `$emit('conexao-updated')`
    // do menu interno se perde junto com o unmount; o canal GlobalBus
    // emitido pelo ConexaoActionsMenu em paralelo garante o refresh.
    GlobalBus.$on(MODAL.ATENDIMENTO_POPUP, this.openFromBus)
    this._unregisterGlobalBus = registerAtendimentoListeners(GlobalBus, {
      onHistoricoImportado: this.onHistoricoImportado,
      onConversaCriada: this.onConversaCriada,
      onConexaoConectada: this.onConexaoConectada,
      onConexaoAtualizada: this.onConexaoAtualizada,
    })
  },
  mounted() {
    this.startDialogObserver()
    this.computeZIndex()
    // Se o popup remonta com isOpen=true (HMR no dev, ou volta de uma
    // troca de rota que tirou o popup do DOM), o poller anterior foi
    // destruído em beforeDestroy. Sem reiniciar aqui, o popup aparece
    // visualmente aberto mas sem polling — e só voltaria a pollar se o
    // user clicasse pra fechar e abrir de novo. `startPolling` é
    // idempotente (`running` guard).
    if (this.isOpen) this.startPolling()
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.ATENDIMENTO_POPUP, this.openFromBus)
    if (this._unregisterGlobalBus) {
      this._unregisterGlobalBus()
      this._unregisterGlobalBus = null
    }
    this.stopPolling()
    this.stopDialogObserver()
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    if (this._syncTimer) this._syncTimer.stop()
  },
  methods: {
    toggle() {
      if (this.isOpen) {
        this.close()
      } else {
        this.open()
      }
    },

    async open() {
      this.refreshZIndex()
      this.isOpen = true
      this.ensureConversasLoaded()
      await this.ensureConexoesLoaded()
      this.startPolling()
      // Re-gatilha `syncChats` nas conexões conectadas (mesmo padrão do
      // view-atendimento.initializeModule). Sem isso, quando o user pareia
      // pela primeira vez no popup e o backend ainda está populando o DB,
      // o sync pode "perder" a janela — as conversas só aparecem quando
      // ele abre a view cheia. Agora o popup gatilha o mesmo fluxo.
      this.syncAllContatos()
    },

    syncAllContatos() {
      // fireSyncChatsForConectadas: fire-and-forget `syncChats` em cada
      // conexão conectada. Retorna N conectadas — se 0, nada a fazer.
      const count = fireSyncChatsForConectadas(apiAtendimento, this.conexoes)
      if (count === 0) return

      this.syncingChats = true

      // Timer de sync: 12 × 10s = ~2 min de listarConversas silent enquanto
      // o backend popula o DB. Re-chamadas (via retries do QR connect)
      // reiniciam o timer — o factory cuida do clearInterval anterior.
      // (Popup usa 12 × 10s; view usa 6 × 10s — popup fica aberto por
      // mais tempo, então mantém o sync ativo por mais tempo.)
      if (!this._syncTimer) {
        this._syncTimer = createSyncReloadTimer(this.$store, {
          maxTries: 12,
          intervalMs: 10000,
          silent: true,
          onEnd: () => {
            this.syncingChats = false
          },
        })
      }
      this._syncTimer.start()

      // Primeiro reload também silencioso — `ensureConversasLoaded` no
      // open() já fez a carga inicial com loading visível.
      this.$store.dispatch('atendimento/listarConversas', { _silent: true })
    },

    close() {
      this.isOpen = false
      this.pendingClienteApp = null
      this.stopPolling()
      // Recalcula em vez de resetar hardcoded: se ainda há modal aberto
      // (z 202+), o FAB precisa continuar acima dele pro user conseguir
      // reabrir o popup. Se não há modal ativo, refreshZIndex cai no
      // default (3).
      this.refreshZIndex()
    },

    // Observer no body com debounce — v-dialog dispara várias mutations
    // em cascata ao abrir/fechar. Sem debounce, rodávamos computeZIndex
    // ~30x por transição, lendo estados transitórios do DOM. Com 50ms
    // de delay, só uma execução "final" roda quando o DOM se estabiliza.
    startDialogObserver() {
      if (typeof MutationObserver === 'undefined') return
      this.dialogObserver = new MutationObserver(() => {
        this.scheduleRefresh()
      })
      this.dialogObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      })
    },

    stopDialogObserver() {
      if (this.dialogObserver) {
        this.dialogObserver.disconnect()
        this.dialogObserver = null
      }
    },

    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer)
      this.refreshTimer = setTimeout(() => this.computeZIndex(), 50)
    },

    refreshZIndex() {
      this.scheduleRefresh()
    },

    // Calcula z-index + contagens de dialogs lendo o DOM:
    //   - 0 dialogs → zIndex default, popup visível
    //   - Só "crm-root-modal" abertos → popup acima deles
    //   - Modal "acessório" ativo (termos, QR, confirmação) → popup some
    //   - Modal "interno do popup" (foto do contato) → popup atrás do overlay
    computeZIndex() {
      const DEFAULT_Z = 6
      const dialogs = document.querySelectorAll('.v-dialog__content--active')
      this.activeDialogCount = dialogs.length
      this.nonRootDialogCount = countNonRootDialogs(dialogs)
      if (dialogs.length === 0) {
        this.zIndex = DEFAULT_Z
        return
      }
      const hasInnerModal = Array.from(dialogs).some(
        (d) =>
          d.classList.contains('atendimento-inner-modal') ||
          !!d.querySelector('.atendimento-inner-modal')
      )
      if (hasInnerModal) {
        this.zIndex = DEFAULT_Z
        return
      }
      const maxZ = maxDialogZ(dialogs)
      this.zIndex = maxZ === -Infinity ? DEFAULT_Z : maxZ + 1
    },

    ensureConversasLoaded() {
      // Sempre força um reload ao abrir o popup — a lista pode estar
      // stale (sync em background após conectar via QR, polling perdido,
      // etc.). Vuex mantém o cache até aqui, então só a primeira abertura
      // paga o custo de request; reaberturas fazem refresh silencioso.
      this.$store.dispatch('atendimento/listarConversas')
    },

    async ensureConexoesLoaded() {
      if (this.hasConexoes) return
      await this.reloadConexoes()
    },

    // Fetch leve da lista de conexões — sem side-effects em conversas.
    // Usado no retry loop de `onConexaoConectada` (mirror da view), onde
    // `reloadConexoes` seria redundante: `syncAllContatos` já cuida do
    // listarConversas silent logo depois.
    async loadConexoes() {
      this.conexoes = await fetchConexoes(apiAtendimento)
    },

    // Força reload da lista de conexões + conversas — usado após desconectar,
    // desativar ou remover permanentemente (emit `conexao-updated` do
    // ConversationList), e após parear via QR Code. Remover permanentemente
    // apaga histórico no backend de forma assíncrona, então nesse caso
    // `scheduleDeletedConversasRetries` (retries em 1s e 3s silent) evita
    // conversas fantasmas mesmo se o backend levar alguns instantes.
    async reloadConexoes(payload = {}) {
      this.conexoes = await fetchConexoes(apiAtendimento)
      const selected = this.selectedConversa
      if (
        selected &&
        pickSelectedIfConexaoRemovida(selected, this.conexoes) === null
      ) {
        this.$store.dispatch('atendimento/selectConversa', null)
      }
      const action = payload && payload.action
      // Ações destrutivas invalidam o sync em andamento — para o timer
      // pra não continuar chamando listarConversas após desconectar/
      // desativar/remover.
      if (isDestructiveConexaoAction(action)) {
        if (this._syncTimer) this._syncTimer.stop()
        this.syncingChats = false
      }
      this.$store.dispatch('atendimento/listarConversas')
      if (action === 'deleted') {
        // Popup usa retries silenciosos — diferente da view (que usa
        // loading visível), porque o painel do popup pode estar
        // desmontado quando os retries disparam (MutationObserver +
        // shouldShowPopup), e piscar skeleton num painel intermitente
        // era a UX que a gente estava evitando.
        scheduleDeletedConversasRetries(this.$store, { silent: true })
      }
    },

    // Handler do item "Recarregar conversas" do ConexaoActionsMenu (menu ⋮)
    // dentro do ConversationList do popup. Mirror do
    // `onRefreshChatsListFromList` da view-atendimento, exceto que
    // `activeConexaoFilter` é null (popup não tem filtro de conexão
    // visível — §5.4 do REQUIREMENTS).
    async onRefreshChatsListFromList() {
      if (this.refreshingChats) return
      this.refreshingChats = true

      this.$showSnack({
        text: 'Atualizando conversas...',
        color: null,
        button: false,
      })

      const user = this.$currentUser() || {}
      try {
        const result = await fireRefreshChatsListForVisible(
          apiAtendimento,
          this.conexoes,
          {
            // Store popula `id_interno` via SET_CURRENT_USER (spread de
            // payload) — `user.id` NÃO existe consistentemente. O backend
            // identifica o profissional via id_interno em
            // atendimento_conexao_profissional.id_profissional.
            idProfissional: user.id_interno,
            isGestor: !!user.IS_GESTOR,
            activeConexaoFilter: null,
          }
        )
        this.$store.dispatch('atendimento/listarConversas')
        // buildRefreshFeedback centraliza copy + pluralização PT-BR.
        // Idêntico ao handler do view-atendimento (zero duplicação).
        this.$showSnack(buildRefreshFeedback(result))
      } finally {
        this.refreshingChats = false
      }
    },

    // Disparado pelo ModalQrcodeConexao quando o WhatsApp é pareado. O
    // status muda de 'conectando'/'desconectado' → 'conectado' no backend
    // de forma assíncrona (webhook da Evolution API). Os retries
    // escalonados [0, 1500, 4000] garantem que pelo menos um dos 3 reloads
    // pegue o status final como 'conectado' e dispare o syncAllContatos.
    //
    // Usa `loadConexoes` (fetch leve, sem listarConversas) em vez de
    // `reloadConexoes` — mirror da view-atendimento. `syncAllContatos`
    // já dispara `listarConversas` silent e roda o timer. Sem isso,
    // cada retry piscaria o skeleton.
    onConexaoConectada() {
      createConnectRetries(
        async () => {
          await this.loadConexoes()
          this.syncAllContatos()
        },
        { delays: [0, 1500, 4000] }
      )
    },

    // Disparado pelo ConexaoActionsMenu (via GlobalBus) após qualquer ação
    // bem-sucedida em uma conexão (reiniciar/desconectar/desativar/remover).
    // Cobre o caso em que o painel do popup está desmontado por v-dialog
    // aberto (ex: $showModal de confirmação de remover) e o `$emit` local
    // da cadeia ConexaoActionsMenu → ConversationList → popup se perde.
    onConexaoAtualizada(payload = {}) {
      this.reloadConexoes(payload)
    },

    startPolling() {
      // Mantém o guard do original ("já está rolando, não reinicia").
      if (this._poller && this._poller.running) return
      if (!this._poller) {
        this._poller = createConversasPoller(this.$store, {
          intervalMs: POLL_INTERVAL_MS,
          getIsSyncing: () => this.syncingChats,
          silentDuringSync: true,
          // Sem onPoll/maxConsecutiveErrors/checkVisibility — usa os
          // defaults do service. Popup fica em primeiro plano do CRM
          // (sem checkVisibility) e nunca para por erros (Infinity).
          // O delta de mensagens + auto-mark-as-read vêm do default.
        })
      }
      this._poller.start()
    },

    stopPolling() {
      if (this._poller) this._poller.stop()
    },

    onSelectConversa(conversa) {
      // ConversationList emite @select mas não dispatcha — o handler aqui
      // é quem abre a conversa (mesmo padrão de view-atendimento.vue).
      if (!conversa) return
      this.$store.dispatch('atendimento/selectConversa', conversa)
      this.$store.dispatch('atendimento/listarMensagens', {
        id_conversa: conversa.id,
      })
      if (conversa.mensagens_nao_lidas > 0) {
        this.$store.dispatch('atendimento/marcarComoLida', {
          id_conversa: conversa.id,
        })
      }
    },

    // Abre a view cheia de atendimento. Como `selectedConversa` vive no
    // Vuex, ao chegar em /crm/comunicacoes/atendimento (rota crm.atendimento,
    // agrupada sob a aba Omnichannel) o `initializeModule()` detecta e já
    // carrega mensagens + dados do cliente (ver view-atendimento
    // initializeModule, linha ~179-185).
    // Guard: o botão só existe quando `canOpenFullAtendimento` é true, mas
    // mantemos a guarda aqui também pra defender contra disparos via atalho
    // ou chamada externa em um estado onde o user nao tem FEATURES.CRM.
    openFullAtendimento() {
      if (!this.canOpenFullAtendimento) return
      if (this.$route && this.$route.name === 'crm.atendimento') {
        this.close()
        return
      }
      this.close()
      this.$router.push({ name: 'crm.atendimento' })
    },

    voltarParaLista() {
      this.$store.dispatch('atendimento/selectConversa', null)
    },

    onImportHistory() {
      if (!this.selectedConversa) return
      const contato = this.selectedConversa.contato
      GlobalBus.$emit(MODAL.ATENDIMENTO_IMPORTAR_HISTORICO, {
        id_conversa: this.selectedConversa.id,
        nome: contato ? contato.nome : '',
      })
    },

    async onHistoricoImportado() {
      if (!this.selectedConversa) return
      await this.$store.dispatch('atendimento/listarMensagens', {
        id_conversa: this.selectedConversa.id,
      })
    },

    // Delega a chain (select + listarMensagens + marcarComoLida condicional +
    // pollConversas) pra action compartilhada. Ver comentário em
    // atendimento/store/actions.js handleConversaCriada sobre o motivo de
    // NÃO chamar listarConversas.
    onConversaCriada(data) {
      this.$store.dispatch('atendimento/handleConversaCriada', data)
    },

    openFromBus(payload) {
      const p = payload || {}
      const id_cliente_app = p.id_cliente_app || null
      // `silent: true` → só pré-seleciona a conversa do cliente, não abre
      // o painel. Útil pra modais de detalhe de lead/cliente que querem
      // preparar o popup sem obstruir a view atual (quando o user depois
      // clicar o FAB, já vem na conversa certa).
      const silent = !!p.silent
      // Recalcula z-index mesmo no silent — se o modal de lead acabou de
      // abrir (e emitiu silent), queremos que o FAB já fique acima dele
      // pra o user conseguir clicar sem o modal esconder o botão.
      // $nextTick garante que o v-dialog já está no DOM com seu z-index.
      this.$nextTick(() => this.refreshZIndex())
      if (!silent) {
        this.open()
      } else if (!this.conversas || this.conversas.length === 0) {
        // Silent + sem conversas carregadas: dispara o fetch em background
        // pra que o watcher possa resolver quando chegarem.
        this.ensureConversasLoaded()
      }
      if (!id_cliente_app) return
      if (!this.conversas || this.conversas.length === 0) {
        this.pendingClienteApp = id_cliente_app
        return
      }
      this.trySelectByClienteApp(id_cliente_app)
    },

    trySelectByClienteApp(id_cliente_app) {
      const conversa = (this.conversas || []).find(
        (c) => c && c.contato && c.contato.id_cliente_app === id_cliente_app
      )
      if (conversa) {
        this.$store.dispatch('atendimento/selectConversa', conversa)
        this.$store.dispatch('atendimento/listarMensagens', {
          id_conversa: conversa.id,
        })
      }
      this.pendingClienteApp = null
    },
  },
}
</script>

<template>
  <div v-if="shouldRender" class="atendimento-popup">
    <!-- FAB flutuante — sempre visível exceto quando há modal nested
         (2+ dialogs ativos). O v-show preserva o state interno. -->
    <v-btn
      v-show="shouldShowPopup"
      fab
      color="primary"
      class="atendimento-popup__fab az-shadow"
      data-test="atendimento-popup-fab"
      :style="{ zIndex }"
      :aria-label="toggleAriaLabel"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="toggle"
    >
      <icon-svg
        :name="isOpen ? 'close' : 'messages-2'"
        :size="isOpen ? '26' : '32'"
        color="#FFFFFF"
      />
    </v-btn>

    <!-- Mini painel (lista OU chat, derivado de selectedConversa). -->
    <transition name="fade-scale">
      <div
        v-if="isOpen && shouldShowPopup"
        class="atendimento-popup__panel az-card rounded-sm no-padding"
        data-test="atendimento-popup-panel"
        :style="{ ...panelCssVars, zIndex }"
      >
        <div class="atendimento-popup__header d-flex align-center px-4">
          <v-btn
            v-if="hasSelectedConversa"
            icon
            small
            class="mr-2"
            data-test="atendimento-popup-back"
            aria-label="Voltar para a lista"
            @click="voltarParaLista"
          >
            <icon-svg name="mdi-arrow-left" size="18" color="#8E8E8E" />
          </v-btn>
          <span class="az-text-primary az-size-14 font-medium flex-grow-1">
            Mensagens
            <v-tooltip v-if="syncingChats" top>
              <template v-slot:activator="{ on }">
                <v-progress-circular
                  indeterminate
                  size="12"
                  width="2"
                  color="primary"
                  class="ml-1"
                  data-test="popup-sync-indicator"
                  v-on="on"
                />
              </template>
              <span>
                Sincronizando conversas recentes do WhatsApp. Isso pode levar
                alguns segundos.
              </span>
            </v-tooltip>
          </span>
          <v-tooltip v-if="canOpenFullAtendimento" top>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                small
                class="mr-1"
                data-test="atendimento-popup-expand"
                aria-label="Expandir mensagens"
                v-on="on"
                @click="openFullAtendimento"
              >
                <icon-svg name="mdi-open-in-new" size="16" color="#8E8E8E" />
              </v-btn>
            </template>
            <span>Abrir atendimento completo</span>
          </v-tooltip>
          <icon-svg name="messages-2" size="16" color="primary" />
        </div>

        <div class="atendimento-popup__body">
          <ConversationList
            v-if="!hasSelectedConversa"
            :conexoes="conexoes"
            :refreshing="refreshingChats"
            panelStyle="flatWhite"
            empty-state-rich
            class="atendimento-popup__list"
            @select="onSelectConversa"
            @conexao-updated="reloadConexoes"
            @refresh-chats-list="onRefreshChatsListFromList"
          />
          <div v-else class="atendimento-popup__chat">
            <ChatWindow
              :conexoes="conexoes"
              :has-conexoes="hasConexoes"
              :show-close-conversa="false"
              @import-history="onImportHistory"
            />
          </div>
        </div>
      </div>
    </transition>

    <ModalNovaConversa />
    <ModalImportarHistorico />
    <ModalGerenciarRespostas />
    <ModalRespostaRapida />
    <ModalTransferirConversa />
    <ModalVincularContato />
  </div>
</template>

<style lang="sass" scoped>
// Item de flex normal dentro de #aawz-floating-dock (main.vue) — não é
// position:fixed próprio. `position: relative` dá o containing block pro
// painel (.atendimento-popup__panel, position:absolute), que assim segue o
// FAB onde quer que o flexbox o posicione, sem cálculo manual de pixel.
.atendimento-popup
  position: relative

.atendimento-popup__fab.v-btn
  width: 71px
  height: 71px

.atendimento-popup__panel
  position: absolute
  bottom: calc(100% + 10px)
  right: 0
  width: 400px
  height: 600px
  max-height: 80vh
  background: var(--panel-bg)
  display: flex
  flex-direction: column
  overflow: hidden

.atendimento-popup__header
  border-bottom: 1px solid var(--gray-3)
  flex-shrink: 0
  // Altura fixa — não muda entre modos lista/chat (com ou sem botão voltar).
  min-height: 52px

.atendimento-popup__back
  background: none
  border: none
  cursor: pointer
  padding: 4px
  border-radius: 4px
  display: inline-flex
  align-items: center
  justify-content: center
  line-height: 1

  &:hover
    background-color: rgba(0, 0, 0, 0.06)

.atendimento-popup__body
  flex-grow: 1
  min-height: 0
  display: flex
  flex-direction: column
  overflow: hidden

.atendimento-popup__list
  flex-grow: 1
  overflow-y: auto
  // Padding 16px horizontal + 12px top pra separar o search do header
  // "Mensagens" (sem o top ficava colado na borda do header).
  padding: 12px 16px 0

  // ConversationList usa input-by-type com width natural (herdado de
  // style="min-width: 175px" default). No popup, força full-width pra
  // search e demais controles ocuparem toda a largura disponível.
  ::v-deep .v-input,
  ::v-deep .v-text-field,
  ::v-deep .v-input__slot
    width: 100% !important
    max-width: 100% !important
    min-width: 0 !important

.atendimento-popup__chat
  flex-grow: 1
  min-height: 0
  display: flex
  flex-direction: column
  overflow: hidden
  // Respiro entre o header do popup e o ChatWindow (header interno da
  // conversa fica muito colado sem isso).
  padding-top: 8px

// Transição de abertura: suave (fade + scale).
.fade-scale-enter-active,
.fade-scale-leave-active
  transition: opacity 0.15s ease, transform 0.15s ease
  transform-origin: bottom right

.fade-scale-enter,
.fade-scale-leave-to
  opacity: 0
  transform: scale(0.95)
</style>
